/**
 * Copyright 2015 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __extends } from "tslib";
import { AVM1PropertyDescriptor } from '../runtime/AVM1PropertyDescriptor';
import { alIsArray, alCoerceString, alDefineObjectProperties, alNewObject } from '../runtime';
import { wrapAVM1NativeClass } from './AVM1Utils';
import { AVM1Object } from '../runtime/AVM1Object';
import { AVM1ArrayNative } from '../natives';
var _sharedObjectStorage;
var VirtualStorage = /** @class */ (function () {
    function VirtualStorage() {
        this._values = {};
        this._realStorage = null;
    }
    VirtualStorage.prototype.getItem = function (key) {
        if (this._realStorage) {
            this._values[key] = this._realStorage.getItem(key);
        }
        return this._values[key];
    };
    VirtualStorage.prototype.setItem = function (key, value) {
        if (this._realStorage) {
            this._realStorage.setItem(key, value);
        }
        this._values[key] = value;
    };
    VirtualStorage.prototype.removeItem = function (key) {
        if (this._realStorage) {
            this._realStorage.removeItem(key);
        }
        delete this._values[key];
    };
    return VirtualStorage;
}());
export function getSharedObjectStorage() {
    if (!_sharedObjectStorage) {
        _sharedObjectStorage = new VirtualStorage();
        if (typeof Storage !== 'undefined') {
            try {
                _sharedObjectStorage._realStorage = window.localStorage;
            }
            catch (e) {
                console.warn('[Shared Storage] ', e);
            }
        }
    }
    return _sharedObjectStorage;
}
var SharedObjectDebug = /** @class */ (function () {
    function SharedObjectDebug() {
    }
    SharedObjectDebug.decodedData = function () {
        var values = getSharedObjectStorage()._values;
        var raw = {};
        for (var key in values) {
            raw[key] = JSON.parse(values[key]);
        }
        this._lastRawData = raw;
        return raw;
    };
    SharedObjectDebug.encodeAndApplyData = function () {
        if (!this._lastRawData) {
            throw 'Need call decode before encode for detecting a model';
        }
        var store = getSharedObjectStorage();
        for (var key in this._lastRawData) {
            store.setItem(key, JSON.stringify(this._lastRawData[key]));
        }
        return store._values;
    };
    SharedObjectDebug._lastRawData = null;
    return SharedObjectDebug;
}());
export { SharedObjectDebug };
var AVM1SharedObject = /** @class */ (function (_super) {
    __extends(AVM1SharedObject, _super);
    function AVM1SharedObject(context) {
        var _this = _super.call(this, context) || this;
        _this._flushPending = 0;
        alDefineObjectProperties(_this, {
            data: {
                get: _this.getData,
                enumerable: true,
            },
            clear: {
                value: _this.clear,
                writable: true
            },
            flush: {
                value: _this.flush,
                writable: true
            }
        });
        _this._bindedFlush = _this.flush.bind(_this);
        return _this;
    }
    AVM1SharedObject.createAVM1Class = function (context) {
        return wrapAVM1NativeClass(context, true, AVM1SharedObject, ['getLocal'], ['data#', 'flush', 'clear']);
    };
    AVM1SharedObject.prototype.setName = function (name) {
        name = alCoerceString(this.context, name);
        this._storage_name = name;
        var rawData = getSharedObjectStorage().getItem(name);
        if (rawData) {
            try {
                this._data = this.getAVM1Value(JSON.parse(rawData));
                return;
            }
            catch (e) {
                console.warn('[AVM1 SharedObject]', 'Invalid data:', rawData);
            }
        }
        this._data = alNewObject(this.context);
        console.warn('[AVM1 SharedObject]', 'No shared object found');
        return null; //context.sec.flash.external.ExternalInterface.axClass.available;
    };
    AVM1SharedObject.getLocal = function (context, name, localPath, secure) {
        if (localPath || secure) {
            console.warn('SharedObject.getLocal: params \'localPath\' and \'secure\' not supported');
        }
        //localPath = alCoerceString(this.context, localPath);
        //secure = alToBoolean(this.context, secure);
        var newSharedObj = new AVM1SharedObject(context);
        newSharedObj.setName(name);
        return newSharedObj;
    };
    AVM1SharedObject.prototype.applyDataHook = function (data) {
        var _this = this;
        if (data['__alPutHookApplyed']) {
            return data;
        }
        data['__alPutHookApplyed'] = true;
        data.alPut = function (p, v) {
            AVM1Object.prototype.alPut.call(data, p, v);
            _this.requestFlush();
        };
        return data;
    };
    AVM1SharedObject.prototype.requestFlush = function () {
        clearTimeout(this._flushPending);
        this._flushPending = setTimeout(this._bindedFlush, 100);
    };
    AVM1SharedObject.prototype.getData = function () {
        return this.applyDataHook(this._data);
    };
    AVM1SharedObject.prototype.clear = function () {
        getSharedObjectStorage().removeItem(this._storage_name);
    };
    AVM1SharedObject.prototype.getAVM1Value = function (jsValue) {
        if (typeof jsValue === 'number' ||
            typeof jsValue === 'string' ||
            typeof jsValue === 'boolean') {
            return jsValue;
        }
        if (Array.isArray(jsValue)) {
            for (var i = 0; i < jsValue.length; i++) {
                jsValue[i] = this.getAVM1Value(jsValue[i]);
            }
            return new AVM1ArrayNative(this.context, jsValue);
        }
        if (typeof jsValue === 'object') {
            var newAVM1Obj = alNewObject(this.context);
            for (var key in jsValue) {
                newAVM1Obj.alSetOwnProperty(key, new AVM1PropertyDescriptor(64 /* DATA */, this.getAVM1Value(jsValue[key])));
            }
            return newAVM1Obj;
        }
    };
    AVM1SharedObject.prototype.getJSValue = function (avm1value) {
        if (typeof avm1value === 'number' || typeof avm1value === 'string' || typeof avm1value === 'boolean') {
            return avm1value;
        }
        if (alIsArray(this.context, avm1value)) {
            console.log('array');
            var jsArray = avm1value.value;
            var arr = [];
            if (jsArray) {
                var arrLength = jsArray.length;
                for (var i = 0; i < arrLength; i++) {
                    arr[i] = this.getJSValue(jsArray[i]);
                }
            }
            return arr;
        }
        if (avm1value instanceof AVM1Object) {
            var jsValue = {};
            for (var key in avm1value._ownProperties) {
                if (key != '__proto__' && key != '__constructor__') {
                    jsValue[key] = this.getJSValue(avm1value._ownProperties[key].value);
                }
            }
            return jsValue;
        }
        console.log('unknown datatype');
    };
    AVM1SharedObject.prototype.flush = function (_minDiskSpace) {
        if (this._flushPending) {
            clearTimeout(this._flushPending);
            this._flushPending = null;
        }
        //minDiskSpace = alCoerceNumber(this.context, minDiskSpace);
        //this._as3SharedObject.flush(minDiskSpace);
        var jsValue = this.getJSValue(this._data);
        getSharedObjectStorage().setItem(this._storage_name, JSON.stringify(jsValue));
        return false; // can be a string 'pending' or boolean
    };
    return AVM1SharedObject;
}(AVM1Object));
export { AVM1SharedObject };
